import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Compass, Plus, Trash2, Mail, Calendar, Users, AlertTriangle, CreditCard } from "lucide-react";
import api, { formatError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BASE_ISLANDS, V3_ISLANDS } from "@/lib/islands";

const ALL_ISLANDS = [...BASE_ISLANDS, ...V3_ISLANDS];
const CAT_LABEL = { activite: "tActivity", restaurant: "tRestaurant", lieu: "tPlace", decouverte: "tDiscovery" };

export default function PartnerDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState({ profile: {}, activities: [], requests: [] });
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [connect, setConnect] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState({ name: "", category: "activite", country: "", description: "", price_from: "", image: "", images: [], schedule: "", meeting_point: "", duration: "" });

  const load = () => api.get("/partner/dashboard").then((r) => setData(r.data)).catch(() => {});
  const loadConnect = () => api.get("/partner/connect/status").then((r) => setConnect(r.data)).catch(() => {});
  const loadPayouts = () => api.get("/partner/payouts").then((r) => setPayouts(r.data.payouts)).catch(() => {});
  useEffect(() => { load(); loadConnect(); loadPayouts(); }, []);

  const startConnect = async () => {
    try { const { data } = await api.post("/partner/connect/onboard", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image: s.image || r.data.url, images: [...(s.images || []), r.data.url] }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name) { toast.error(t("partnerDash.nameRequired")); return; }
    try {
      await api.post("/partner/activities", { ...form, price_from: Number(form.price_from) || 0 });
      toast.success(t("partnerDash.activityAdded"));
      setOpen(false);
      setForm({ name: "", category: "activite", country: "", description: "", price_from: "", image: "", images: [], schedule: "", meeting_point: "", duration: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/partner/activities/${id}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setReqStatus = async (id, status) => {
    try { await api.patch(`/partner/requests/${id}`, { status }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const badge = { new: "bg-canard/15 text-canard-dark", contacted: "bg-yellow-100 text-yellow-700", closed: "bg-gray-100 text-gray-500", confirmed: "bg-emeraude/15 text-emeraude-dark", pending_payment: "bg-orange-100 text-orange-700" };

  return (
    <div className="section-sand min-h-[80vh] py-12 px-4" data-testid="partner-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-canard/10 flex items-center justify-center"><Compass className="w-6 h-6 text-canard" /></div>
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-ink" data-testid="partner-welcome">{t("partnerDash.welcome", { name: data.profile?.name || "" })}</h1>
            <p className="text-muted2 font-body text-sm">{data.profile?.company_name} · {data.profile?.partner_type}</p>
          </div>
        </div>

        {data.profile?.must_change_password && (
          <div data-testid="partner-pwd-reminder" className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-2xl p-4 my-5">
            <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800 font-body">{t("partnerDash.pwdReminder")} <Link to="/profile" data-testid="partner-goto-profile" className="font-bold underline">{t("partnerDash.goProfile")}</Link></p>
          </div>
        )}

        <div data-testid="partner-connect-card" className="bg-white rounded-2xl border border-border p-5 my-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-ink flex items-center gap-2"><CreditCard className="w-5 h-5 text-canard" /> {t("partnerDash.stripeTitle")}</p>
            <p className="text-sm text-muted2 font-body mt-1">{connect?.charges_enabled ? t("partnerDash.stripeVerified") : t("partnerDash.stripePending")}</p>
          </div>
          {connect?.charges_enabled ? (
            <span data-testid="partner-connect-ok" className="text-xs font-bold px-3 py-1 rounded-full bg-emeraude/15 text-emeraude-dark shrink-0">✓ {t("partnerDash.stripeConnected")}</span>
          ) : (
            <Button data-testid="partner-connect-btn" onClick={startConnect} className="rounded-full bg-emeraude hover:bg-emeraude-dark font-bold h-10 shrink-0"><CreditCard className="w-4 h-4 mr-1" /> {connect?.connected ? t("partnerDash.stripeContinue") : t("partnerDash.stripeConnect")}</Button>
          )}
        </div>

        <div className="flex items-center justify-between mt-8 mb-4">
          <h2 className="font-heading font-bold text-xl text-ink">{t("partnerDash.myActivities")}</h2>
          <Button data-testid="partner-add-activity" onClick={() => setOpen(true)} className="rounded-full bg-emeraude hover:bg-emeraude-dark font-bold h-10"><Plus className="w-4 h-4 mr-1" /> {t("partnerDash.addActivity")}</Button>
        </div>
        {data.activities.length === 0 ? (
          <p data-testid="partner-no-activities" className="text-muted2 font-body text-center py-10 bg-white rounded-2xl border border-border">{t("partnerDash.noActivities")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.activities.map((a) => (
              <div key={a.id} data-testid={`partner-activity-${a.id}`} className="bg-white rounded-2xl border border-border overflow-hidden shadow-card">
                <img src={fileUrl(a.image)} alt={a.name} className="w-full h-36 object-cover" />
                <div className="p-4">
                  <p className="font-heading font-bold text-ink">{a.name}</p>
                  <p className="text-xs text-muted2 mt-0.5">{a.country || "—"} · {a.category}</p>
                  <p className="text-sm text-muted2 mt-1 line-clamp-2">{a.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm font-bold text-canard">{t("activities.from")} {a.price_from}€</span>
                    <Button size="sm" variant="outline" data-testid={`partner-del-activity-${a.id}`} onClick={() => del(a.id)} className="rounded-full h-8 text-destructive border-destructive/30"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-heading font-bold text-xl text-ink mt-10 mb-4">{t("partnerDash.requests")}</h2>
        {data.requests.length === 0 ? (
          <p data-testid="partner-no-requests" className="text-muted2 font-body text-center py-10 bg-white rounded-2xl border border-border">{t("partnerDash.noRequests")}</p>
        ) : (
          <div className="space-y-3">
            {data.requests.map((r) => (
              <div key={r.id} data-testid={`partner-request-${r.id}`} className="bg-white rounded-2xl border border-border p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading font-bold text-ink">{r.activity_name}</p>
                    <p className="text-sm text-muted2 mt-1 flex flex-wrap gap-3">
                      <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {r.name} · {r.email}</span>
                      {r.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {r.date}</span>}
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.people}</span>
                    </p>
                    {r.message && <p className="text-sm text-ink font-body mt-2">{r.message}</p>}
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${badge[r.status] || badge.new}`}>{t(`partnerAdmin.${r.status}`)}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" data-testid={`partner-req-contacted-${r.id}`} onClick={() => setReqStatus(r.id, "contacted")} className="rounded-full h-8 text-xs">{t("partnerAdmin.markContacted")}</Button>
                  <Button size="sm" variant="outline" data-testid={`partner-req-closed-${r.id}`} onClick={() => setReqStatus(r.id, "closed")} className="rounded-full h-8 text-xs">{t("partnerAdmin.markClosed")}</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-heading font-bold text-xl text-ink mt-10 mb-4">{t("partnerDash.myPayouts")}</h2>
        {payouts.length === 0 ? (
          <p data-testid="partner-no-payouts" className="text-muted2 font-body text-center py-10 bg-white rounded-2xl border border-border">{t("partnerDash.poEmpty")}</p>
        ) : (
          <div className="space-y-2">
            {payouts.map((p) => {
              const label = p.status === "paid_out" ? t("partnerDash.poReceived") : p.status === "direct" ? t("partnerDash.poDirect") : t("partnerDash.poPending");
              const cls = p.status === "pending" ? "bg-orange-100 text-orange-700" : "bg-emeraude/15 text-emeraude-dark";
              return (
                <div key={p.id} data-testid={`partner-payout-${p.id}`} className="bg-white rounded-2xl border border-border p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-bold text-ink text-sm">{p.activity_name}</p>
                    <p className="text-xs text-muted2 mt-0.5">{t("partnerDash.poNet")}: <span className="font-bold text-canard-dark">{p.net} €</span> · {p.amount} €</p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${cls}`}>{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("partnerDash.addActivity")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="pa-name" placeholder={t("partnerDash.fName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <select data-testid="pa-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 w-full rounded-xl bg-sand px-3 text-sm">
              {Object.entries(CAT_LABEL).map(([c, k]) => <option key={c} value={c}>{t(`partnerReq.${k}`)}</option>)}
            </select>
            <select data-testid="pa-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-11 w-full rounded-xl bg-sand px-3 text-sm">
              <option value="">{t("partnerDash.fIsland")}</option>
              {ALL_ISLANDS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
            </select>
            <textarea data-testid="pa-description" placeholder={t("partnerDash.fDesc")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-xl bg-sand p-3 text-sm" />
            <Input data-testid="pa-price" type="number" min="0" placeholder={t("partnerDash.fPrice")} value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="pa-duration" placeholder={t("partnerDash.fDuration")} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="pa-schedule" placeholder={t("partnerDash.fSchedule")} value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <Input data-testid="pa-meeting" placeholder={t("partnerDash.fMeeting")} value={form.meeting_point} onChange={(e) => setForm({ ...form, meeting_point: e.target.value })} className="h-11 rounded-xl bg-sand" />
            <div className="flex items-center gap-2 flex-wrap">
              {(form.images || []).map((im) => <img key={im} src={fileUrl(im)} alt="" className="w-14 h-14 rounded-xl object-cover border border-border" />)}
              {(!form.images || form.images.length === 0) && <div className="w-14 h-14 rounded-xl bg-sand border border-border" />}
              <label data-testid="pa-image-label" className="flex-1 min-w-[120px] cursor-pointer text-center rounded-xl bg-sand hover:bg-pastel/40 border border-dashed border-canard/40 text-canard font-semibold text-sm py-3 transition-colors">
                {uploading ? t("partnerDash.uploading") : t("partnerDash.uploadPhoto")}
                <input data-testid="pa-image" type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
            </div>
            <Button data-testid="pa-submit" onClick={save} disabled={uploading} className="w-full rounded-full bg-canard hover:bg-canard-dark font-bold h-11">{t("partnerDash.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
